import React, { useState } from 'react';

/**
 * StoryPuzzle — A mad-lib style fill-in-the-blank puzzle.
 *
 * data.segments is an array of objects:
 *   { type: 'text', content: 'Some story text...' }
 *   { type: 'dropdown', options: ['Wrong', 'Correct', 'Wrong2'], answer: 'Correct' }
 *
 * Shows the full story with inline dropdowns. User fills in all blanks
 * and clicks "Check" to verify answers.
 */

export default function StoryPuzzle({ data, onSolved }) {
  const segments = data.segments;

  // Track answers for each dropdown (by index in segments array)
  const [answers, setAnswers] = useState({});
  const [wrongIndexes, setWrongIndexes] = useState([]);
  const [correctIndexes, setCorrectIndexes] = useState([]);
  const [finished, setFinished] = useState(false);

  // Get all dropdown indexes
  const dropdownIndexes = segments
    .map((seg, i) => (seg.type === 'dropdown' ? i : null))
    .filter((i) => i !== null);

  const handleAnswerChange = (segmentIndex, value) => {
    setAnswers((prev) => ({ ...prev, [segmentIndex]: value }));
    // Clear wrong/correct state for this dropdown when changed
    setWrongIndexes((prev) => prev.filter((i) => i !== segmentIndex));
    setCorrectIndexes((prev) => prev.filter((i) => i !== segmentIndex));
  };

  const handleSubmit = () => {
    // Check all dropdowns
    const wrong = [];
    const correct = [];
    let allCorrect = true;

    dropdownIndexes.forEach((i) => {
      const segment = segments[i];
      // If allCorrect flag is set, any answer is correct
      if (segment.allCorrect) {
        correct.push(i);
        return;
      }
      if (answers[i] === segment.answer) {
        correct.push(i);
      } else {
        wrong.push(i);
        allCorrect = false;
      }
    });

    setCorrectIndexes(correct);
    setWrongIndexes(wrong);

    if (allCorrect) {
      setFinished(true);
      setTimeout(onSolved, 1200);
    }
  };

  // Check if all dropdowns have been answered
  const allAnswered = dropdownIndexes.every((i) => answers[i] && answers[i] !== '');

  return (
    <div className="puzzle-story">
      <div className="story-scroll">
        <p className="story-text story-madlib">
          {segments.map((seg, i) => {
            if (seg.type === 'text') {
              return <span key={i}>{seg.content}</span>;
            } else if (seg.type === 'dropdown') {
              const isWrong = wrongIndexes.includes(i);
              const isCorrect = correctIndexes.includes(i);
              const isAnswered = answers[i] && answers[i] !== '';

              return (
                <select
                  key={i}
                  className={`story-inline-dropdown ${isWrong ? 'dropdown-wrong' : ''} ${isCorrect ? 'dropdown-correct' : ''} ${isAnswered ? 'dropdown-filled' : ''}`}
                  value={answers[i] || ''}
                  onChange={(e) => handleAnswerChange(i, e.target.value)}
                  disabled={finished}
                >
                  <option value="">______</option>
                  {seg.options.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              );
            }
            return null;
          })}
        </p>

        {!finished && (
          <div className="story-submit-area">
            <button
              className="puzzle-submit"
              onClick={handleSubmit}
              disabled={!allAnswered}
            >
              Lock in my answer!
            </button>
            {wrongIndexes.length > 0 && (
              <p className="puzzle-wrong">Hmm, not quite right... try again! 💭</p>
            )}
          </div>
        )}

        {finished && (
          <div className="story-done">
            <span className="story-done-text">You remember! 💛</span>
          </div>
        )}
      </div>
    </div>
  );
}
